import { memo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import type { OdontogramTool } from '../odontogram.types';
import type { FindingState } from '../../../services/OdontologyController';

interface ToolDefinition {
  id: OdontogramTool;
  label: string;
  description: string;
  symbolClass: string;
  symbol: string;
}

const TOOLS: ToolDefinition[] = [
  {
    id: 'caries',
    label: 'Caries',
    description: 'Trabajo pendiente',
    symbolClass: 'border-red-600 bg-red-500',
    symbol: '',
  },
  {
    id: 'restoration',
    label: 'Restauración realizada',
    description: 'Restauración existente por cara',
    symbolClass: 'border-blue-700 bg-blue-600',
    symbol: '',
  },
  {
    id: 'endodontics',
    label: 'Endodoncia',
    description: 'Tratamiento de conducto sobre la pieza',
    symbolClass: 'border-violet-700 bg-white text-violet-700',
    symbol: '│',
  },
  {
    id: 'bridge',
    label: 'Puente',
    description: 'Seleccione la pieza inicial y la final',
    symbolClass: 'border-amber-700 bg-white text-amber-700',
    symbol: '—',
  },
  {
    id: 'absent',
    label: 'Ausente / extraído',
    description: 'Marca la pieza completa',
    symbolClass: 'border-slate-900 bg-white text-slate-900',
    symbol: '×',
  },
  {
    id: 'crown',
    label: 'Corona',
    description: 'Corona sobre la pieza',
    symbolClass: 'rounded-full border-2 border-slate-500 bg-slate-300',
    symbol: '',
  },
  {
    id: 'healthy',
    label: 'Limpiar cara',
    description: 'Deja la cara sana',
    symbolClass: 'border-slate-300 bg-white',
    symbol: '',
  },
  {
    id: 'clearTooth',
    label: 'Restablecer pieza',
    description: 'Limpia toda la pieza',
    symbolClass: 'border-emerald-600 bg-white text-emerald-700',
    symbol: '↺',
  },
];

interface OdontogramToolbarProps {
  selectedTool: OdontogramTool;
  onToolChange: (tool: OdontogramTool) => void;
  findingState: FindingState;
  onFindingStateChange: (state: FindingState) => void;
  icdas: number | null;
  onIcdasChange: (value: number | null) => void;
  bridgeStart: number | null;
  dentitionType: number;
  onDentitionTypeChange: (value: number) => void;
  archFilter: string;
  onArchFilterChange: (value: string) => void;
  readOnly?: boolean;
}

function OdontogramToolbarComponent({
  selectedTool,
  onToolChange,
  findingState,
  onFindingStateChange,
  icdas,
  onIcdasChange,
  bridgeStart,
  dentitionType,
  onDentitionTypeChange,
  archFilter,
  onArchFilterChange,
  readOnly,
}: OdontogramToolbarProps) {
  const stateLocked = selectedTool === 'caries' || selectedTool === 'restoration';

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
      aria-labelledby="odontogram-tools-title"
    >
      <h2 id="odontogram-tools-title" className="sr-only">Selector de diagnóstico</h2>

      <div className={`flex flex-wrap items-end gap-3 rounded-lg bg-white p-2${!readOnly ? ' mb-3' : ''}`}>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-600">Dentición</label>
          <Dropdown
            value={dentitionType}
            disabled={readOnly}
            onChange={(e) => onDentitionTypeChange(e.value)}
            options={[
              { label: 'Permanente', value: 1 },
              { label: 'Mixta', value: 3 },
              { label: 'Temporal', value: 2 },
            ]}
            className="p-inputtext-sm"
            style={{ minWidth: '140px' }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-600">Arcada</label>
          <Dropdown
            value={archFilter}
            onChange={(e) => onArchFilterChange(e.value)}
            options={[
              { label: 'Todo', value: 'all' },
              { label: 'Superior', value: 'upper' },
              { label: 'Inferior', value: 'lower' },
            ]}
            className="p-inputtext-sm"
            style={{ minWidth: '120px' }}
          />
        </div>
        {!readOnly ? (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-600">Estado clínico</label>
              <Dropdown
                value={findingState}
                disabled={stateLocked}
                onChange={(e) => onFindingStateChange(e.value as FindingState)}
                options={[
                  { label: 'A realizar / patología (rojo)', value: 1 },
                  { label: 'Realizado / existente (azul)', value: 2 },
                ]}
                className="p-inputtext-sm"
                style={{ minWidth: '200px' }}
                tooltip={stateLocked ? 'El estado está definido por la herramienta seleccionada' : undefined}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-slate-600">ICDAS II</label>
              <Dropdown
                value={icdas}
                disabled={selectedTool !== 'caries'}
                onChange={(e) => onIcdasChange(e.value)}
                options={[
                  { label: 'Sin especificar', value: null },
                  ...([0, 1, 2, 3, 4, 5, 6].map((v) => ({ label: String(v), value: v }))),
                ]}
                className="p-inputtext-sm"
                style={{ minWidth: '120px' }}
                placeholder="Sin especificar"
              />
            </div>
            {selectedTool === 'bridge' ? (
              <span className="rounded-md bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                {bridgeStart == null ? 'Elegí la pieza inicial' : `Inicio ${bridgeStart}: elegí la pieza final`}
              </span>
            ) : null}
          </>
        ) : null}
      </div>

      {!readOnly ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:flex md:flex-wrap" role="toolbar" aria-label="Herramientas del odontograma">
          {TOOLS.map((tool) => {
            const selected = selectedTool === tool.id;
            return (
              <button
                key={tool.id}
                type="button"
                aria-pressed={selected}
                title={tool.description}
                onClick={() => onToolChange(tool.id)}
                className={[
                  'flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-left transition',
                  'focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1',
                  selected
                    ? 'border-cyan-600 bg-cyan-50 text-cyan-900 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300 hover:bg-slate-50',
                ].join(' ')}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center border text-sm font-black leading-none ${tool.symbolClass}`}
                  aria-hidden="true"
                >
                  {tool.symbol}
                </span>
                <span className="text-xs font-semibold">{tool.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

export const OdontogramToolbar = memo(OdontogramToolbarComponent);
